import { supabase } from '@/lib/supabase';
import { Enquiry, CreateEnquiryInput, UpdateEnquiryInput, EnquiryStats, CommitteeMember, EnquiryAssignment, AssignEnquiryInput } from '@/types/enquiries.types';
import { Database } from '@/types/database.types';

type EnquiryRow = Database['public']['Tables']['enquiries']['Row'];
type EnquiryInsert = Database['public']['Tables']['enquiries']['Insert'];
type EnquiryUpdate = Database['public']['Tables']['enquiries']['Update'];

// Convert database snake_case to camelCase
const convertEnquiryFromDb = (row: EnquiryRow): Enquiry => ({
  id: row.id,
  name: row.name,
  email: row.email,
  subject: row.subject,
  message: row.message,
  status: row.status,
  priority: row.priority,
  adminNotes: row.admin_notes,
  assignedTo: row.assigned_to,
  assignedBy: row.assigned_by,
  assignedAt: row.assigned_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  resolvedAt: row.resolved_at,
});

export const enquiryService = {
  // Create new enquiry (public - from contact form)
  async create(input: CreateEnquiryInput): Promise<{ data: Enquiry | null; error: Error | null }> {
    try {
      const insertData: EnquiryInsert = {
        name: input.name,
        email: input.email,
        subject: input.subject,
        message: input.message,
        status: input.status || 'new',
        priority: input.priority || 'normal',
      };

      const { data, error } = await supabase
        .from('enquiries')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return { data: data ? convertEnquiryFromDb(data) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get all enquiries (admin only)
  async getAll(): Promise<{ data: Enquiry[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('enquiries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { 
        data: data ? data.map(convertEnquiryFromDb) : null, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get enquiry by ID
  async getById(id: string): Promise<{ data: Enquiry | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('enquiries')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return { data: data ? convertEnquiryFromDb(data) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Update enquiry (admin only)
  async update(id: string, input: UpdateEnquiryInput): Promise<{ data: Enquiry | null; error: Error | null }> {
    try {
      const updateData: EnquiryUpdate = {
        ...(input.status && { status: input.status }),
        ...(input.priority && { priority: input.priority }),
        ...(input.adminNotes !== undefined && { admin_notes: input.adminNotes }),
        ...(input.assignedTo !== undefined && { assigned_to: input.assignedTo }),
      };

      const { data, error } = await supabase
        .from('enquiries')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { data: data ? convertEnquiryFromDb(data) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Delete enquiry (admin only)
  async delete(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('enquiries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get enquiries by status
  async getByStatus(status: 'new' | 'in_progress' | 'resolved' | 'closed'): Promise<{ data: Enquiry[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('enquiries')
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { 
        data: data ? data.map(convertEnquiryFromDb) : null, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get enquiry statistics
  async getStats(): Promise<{ data: EnquiryStats | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('enquiries')
        .select('status, priority');

      if (error) throw error;

      const stats: EnquiryStats = {
        total: data?.length || 0,
        new: data?.filter(e => e.status === 'new')?.length || 0,
        inProgress: data?.filter(e => e.status === 'in_progress')?.length || 0,
        resolved: data?.filter(e => e.status === 'resolved')?.length || 0,
        closed: data?.filter(e => e.status === 'closed')?.length || 0,
        highPriority: data?.filter(e => e.priority === 'high')?.length || 0,
        urgent: data?.filter(e => e.priority === 'urgent')?.length || 0,
      };

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get all active committee members
  async getCommitteeMembers(): Promise<{ data: CommitteeMember[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('committee_members')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;

      return {
        data: data?.map(row => ({
          id: row.id,
          name: row.name,
          email: row.email,
          role: row.role,
          department: row.department,
          isActive: row.is_active,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        })) || null,
        error: null
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Assign enquiry to committee member
  async assignEnquiry(input: AssignEnquiryInput): Promise<{ data: Enquiry | null; error: Error | null }> {
    try {
      // Get member details
      const { data: member, error: memberError } = await supabase
        .from('committee_members')
        .select('*')
        .eq('id', input.memberId)
        .single();

      if (memberError) throw memberError;

      // Update enquiry with assignment
      const { data, error } = await supabase
        .from('enquiries')
        .update({
          assigned_to: member.name,
          assigned_by: input.assignedBy,
          assigned_at: new Date().toISOString(),
          status: 'in_progress',
        })
        .eq('id', input.enquiryId)
        .select()
        .single();

      if (error) throw error;

      return { data: data ? convertEnquiryFromDb(data) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get assignment history for an enquiry
  async getAssignmentHistory(enquiryId: string): Promise<{ data: EnquiryAssignment[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('enquiry_assignments')
        .select('*')
        .eq('enquiry_id', enquiryId)
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      return {
        data: data?.map(row => ({
          id: row.id,
          enquiryId: row.enquiry_id,
          assignedToMemberId: row.assigned_to_member_id,
          assignedToName: row.assigned_to_name,
          assignedToEmail: row.assigned_to_email,
          assignedBy: row.assigned_by,
          assignmentNote: row.assignment_note,
          assignedAt: row.assigned_at,
        })) || null,
        error: null
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get all enquiries assigned to a specific committee member
  async getEnquiriesByAssignee(memberName: string): Promise<{ data: Enquiry[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('enquiries')
        .select('*')
        .eq('assigned_to', memberName)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: data ? data.map(convertEnquiryFromDb) : null,
        error: null
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};